import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { WorkbookRun } from "../../../../../api/types/workbookRuns.types";
import HorizonatalTilesInfoBar from "../../../../common/HorizontalTilesInfoBar";
import StepsProgress from "../../../../common/StepsProgress";
import WorkbookRunDuration from "../WorkbookRunDuration";

interface Props {
  workbookRun: WorkbookRun;
}

const WorkbookRunInfoBar = ({ workbookRun }: Props) => {
  const templateName = (
    <TypographyTooltipEllipsis text={workbookRun.templateName} typographyProps={{ variant: "subtitle2" }} />
  );

  return (
    <HorizonatalTilesInfoBar
      items={[
        {
          title: "Workbook Template",
          content: (
            <Box sx={{ display: "flex", width: "100%" }}>
              {workbookRun.isTemplateDeleted === false ? (
                <Link
                  component={RouterLink}
                  to={`../template/${workbookRun.templateId}`}
                  underline="none"
                  sx={{ display: "flex", minWidth: 0, color: "inherit", ":hover": { color: "primary.main" } }}
                >
                  {templateName}
                </Link>
              ) : (
                templateName
              )}
            </Box>
          ),
        },
        {
          title: "Created at",
          content: convertISODateTime(workbookRun.createdAt),
        },
        {
          title: "Started at",
          content: workbookRun.startedAt ? convertISODateTime(workbookRun.startedAt) : "Not started yet",
        },
        {
          title: "Started by",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <UserAvatar size="small" userName={workbookRun.createdBy} />
              <TypographyTooltipEllipsis text={workbookRun.createdBy} typographyProps={{ variant: "subtitle2" }} />
            </Box>
          ),
        },
        {
          title: "Duration",
          content: <WorkbookRunDuration workbookRun={workbookRun} showSeconds={true} />,
        },
        getProgressPart({ workbookRun }),
      ]}
    />
  );
};

const getProgressPart = ({ workbookRun }: { workbookRun: WorkbookRun }) => {
  if (workbookRun.status === "Completed") {
    return {
      title: "Progress",
      content: (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <CheckCircleRoundedIcon color="success" /> <Typography>All</Typography>
        </Box>
      ),
      gridProps: { size: 4, sx: { pr: 2.5 } },
    };
  }
  if (workbookRun.status === "Canceled") {
    return {
      title: "Canceled by",
      content: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar size="small" userName={workbookRun.canceledBy ?? ""} />
          <TypographyTooltipEllipsis text={workbookRun.canceledBy ?? ""} typographyProps={{ variant: "subtitle2" }} />
        </Box>
      ),
      gridProps: { size: 4, sx: { pr: 2.5 } },
    };
  }
  return {
    title: "Progress",
    content: <StepsProgress completed={workbookRun.processedItems} total={workbookRun.reportsSettings.length} />,
    gridProps: { size: 4, sx: { pr: 2.5 } },
  };
};

export default WorkbookRunInfoBar;
